import React from "react";
import type { Language } from "../contexts/types";

type PublicSection = {
  title: string;
  body: React.ReactNode;
};

export type PublicPageCopy = {
  seoTitle: string;
  seoDescription: string;
  title: string;
  subtitle?: React.ReactNode;
  intro?: React.ReactNode[];
  updatedAtLabel?: string;
  updatedAt?: string;
  sections: PublicSection[];
  footerLinks: Array<{
    label: string;
    href: string;
  }>;
};

const SUPPORT_EMAIL = "support@orthoprecision.app";
const PRIVACY_EMAIL = "orthoprecisionapp@gmail.com";
const EFFECTIVE_DATE = "11 de junho de 2026";

const emailLink = (email: string) => (
  <a href={`mailto:${email}`} className="text-cyan-300 hover:text-cyan-200">
    {email}
  </a>
);

const canonicalLinks = {
  support: "/suporte",
  terms: "/terms",
  privacy: "/privacy",
};

export const getPublicPageCopy = (language: Language) => {
  const support: Record<Language, PublicPageCopy> = {
    pt: {
      seoTitle: "Suporte - OrthoPrecision",
      seoDescription:
        "Página pública de suporte da OrthoPrecision com orientações técnicas, contato e políticas relacionadas.",
      title: "Suporte - OrthoPrecision",
      subtitle: (
        <>
          Fale com a nossa equipe pelo e-mail {emailLink(SUPPORT_EMAIL)}.
        </>
      ),
      intro: [
        <p key="intro">
          O OrthoPrecision é uma ferramenta de apoio clínico desenvolvida para
          auxiliar ortopedistas e traumatologistas na organização de pacientes,
          análise de laudos, planejamento cirúrgico, consulta de diretrizes,
          documentação clínica e gestão do fluxo de atendimento. O OrthoPrecision{" "}
          <strong className="text-white">
            não substitui avaliação médica, julgamento profissional, diretrizes
            oficiais, consulta presencial ou responsabilidade do profissional de
            saúde
          </strong>
          .
        </p>,
      ],
      sections: [
        {
          title: "Suporte técnico",
          body: (
            <p>
              Problemas de acesso, erros no aplicativo, falhas no carregamento de
              exames, travamentos ou qualquer comportamento inesperado podem ser
              relatados pelo e-mail {emailLink(SUPPORT_EMAIL)}. Inclua o máximo de
              detalhes possível, como o dispositivo utilizado, a versão do app e os
              passos para reproduzir o problema.
            </p>
          ),
        },
        {
          title: "Conta e acesso profissional",
          body: (
            <p>
              Usuários com dúvidas sobre acesso à conta, período de avaliação,
              assinatura ou restauração de compras podem entrar em contato pelo e-mail{" "}
              {emailLink(SUPPORT_EMAIL)}, informando o e-mail cadastrado e uma
              descrição do problema. Participantes convidados para testes profissionais
              também podem solicitar assistência pelo mesmo canal.
            </p>
          ),
        },
        {
          title: "Assinaturas, planos e pagamentos",
          body: (
            <p>
              As assinaturas do OrthoPrecision podem ser gerenciadas de acordo com a
              plataforma em que foram contratadas. Assinaturas realizadas pelo
              aplicativo para iPhone ou iPad são processadas pela Apple e podem ser
              gerenciadas nas configurações de assinaturas do ID Apple. Assinaturas
              realizadas pelo ambiente web são gerenciadas pelo provedor de pagamento
              utilizado no momento da contratação. Para dúvidas sobre cobrança,
              renovação, alteração de plano, restauração de compras ou cancelamento,
              entre em contato pelo e-mail {emailLink(SUPPORT_EMAIL)}, informando o
              e-mail associado à sua conta.
            </p>
          ),
        },
        {
          title: "Exclusão de conta e dados",
          body: (
            <p>
              Você pode excluir permanentemente sua conta e todos os dados associados
              diretamente pelo aplicativo, em{" "}
              <strong className="text-white">Conta &gt; Excluir minha conta</strong>.
              Essa ação remove seus dados clínicos, arquivos enviados e sua conta de
              autenticação de forma irreversível. Caso prefira, você também pode
              solicitar a exclusão pelo e-mail {emailLink(SUPPORT_EMAIL)}.
            </p>
          ),
        },
        {
          title: "Políticas",
          body: (
            <p>
              Consulte nossa{" "}
              <a href={canonicalLinks.privacy} className="text-cyan-300 hover:text-cyan-200">
                Política de Privacidade
              </a>{" "}
              e nossos{" "}
              <a href={canonicalLinks.terms} className="text-cyan-300 hover:text-cyan-200">
                Termos de Uso
              </a>{" "}
              para mais informações sobre como tratamos seus dados e as condições de
              uso do OrthoPrecision.
            </p>
          ),
        },
      ],
      footerLinks: [
        { label: "Suporte", href: canonicalLinks.support },
        { label: "Termos de Uso", href: canonicalLinks.terms },
        { label: "Política de Privacidade", href: canonicalLinks.privacy },
      ],
    },
    en: {
      seoTitle: "Support - OrthoPrecision",
      seoDescription:
        "Public support page for OrthoPrecision with technical guidance, contact details and related policies.",
      title: "Support - OrthoPrecision",
      subtitle: (
        <>
          Reach our team at {emailLink(SUPPORT_EMAIL)}.
        </>
      ),
      intro: [
        <p key="intro">
          OrthoPrecision is a clinical support tool designed to help orthopedists and
          traumatologists organize patients, review reports, plan surgery, consult
          guidelines, document care and manage the care workflow. OrthoPrecision{" "}
          <strong className="text-white">
            does not replace medical assessment, professional judgment, official
            guidelines, in-person consultation or the responsibility of the healthcare
            professional
          </strong>
          .
        </p>,
      ],
      sections: [
        {
          title: "Technical support",
          body: (
            <p>
              Access issues, app errors, exam loading failures, crashes or any other
              unexpected behavior can be reported to {emailLink(SUPPORT_EMAIL)}.
              Include as much detail as possible, such as the device used, the app
              version and the steps to reproduce the issue.
            </p>
          ),
        },
        {
          title: "Account and professional access",
          body: (
            <p>
              Users with questions about account access, trial periods, subscriptions or
              purchase restoration can contact us at {emailLink(SUPPORT_EMAIL)} and
              include the registered email address plus a description of the problem.
              Professionals invited to validation testing can also request help through
              the same channel.
            </p>
          ),
        },
        {
          title: "Subscriptions, plans and payments",
          body: (
            <p>
              OrthoPrecision subscriptions are managed according to the platform where
              they were purchased. Subscriptions made in the iPhone or iPad app are
              processed by Apple and can be managed from Apple ID subscription
              settings. Subscriptions purchased on the web are handled by the payment
              provider used at checkout. For billing, renewal, plan changes, purchase
              restoration or cancellation questions, contact us at{" "}
              {emailLink(SUPPORT_EMAIL)} and include the email associated with your
              account.
            </p>
          ),
        },
        {
          title: "Account and data deletion",
          body: (
            <p>
              You can permanently delete your account and all associated data directly
              in the app at <strong className="text-white">Account &gt; Delete my
              account</strong>. This action permanently removes your clinical data,
              uploaded files and authentication account. If you prefer, you can also
              request deletion via {emailLink(SUPPORT_EMAIL)}.
            </p>
          ),
        },
        {
          title: "Policies",
          body: (
            <p>
              See our{" "}
              <a href={canonicalLinks.privacy} className="text-cyan-300 hover:text-cyan-200">
                Privacy Policy
              </a>{" "}
              and our{" "}
              <a href={canonicalLinks.terms} className="text-cyan-300 hover:text-cyan-200">
                Terms of Use
              </a>{" "}
              for more information about how we handle your data and the conditions for
              using OrthoPrecision.
            </p>
          ),
        },
      ],
      footerLinks: [
        { label: "Support", href: canonicalLinks.support },
        { label: "Terms of Use", href: canonicalLinks.terms },
        { label: "Privacy Policy", href: canonicalLinks.privacy },
      ],
    },
    es: {
      seoTitle: "Soporte - OrthoPrecision",
      seoDescription:
        "Página pública de soporte de OrthoPrecision con orientación técnica, contacto y políticas relacionadas.",
      title: "Soporte - OrthoPrecision",
      subtitle: (
        <>
          Contacte a nuestro equipo por {emailLink(SUPPORT_EMAIL)}.
        </>
      ),
      intro: [
        <p key="intro">
          OrthoPrecision es una herramienta de apoyo clínico desarrollada para ayudar a
          ortopedistas y traumatólogos en la organización de pacientes, análisis de
          informes, planificación quirúrgica, consulta de directrices, documentación
          clínica y gestión del flujo de atención. OrthoPrecision{" "}
          <strong className="text-white">
            no sustituye la evaluación médica, el juicio profesional, las directrices
            oficiales, la consulta presencial ni la responsabilidad del profesional de
            la salud
          </strong>
          .
        </p>,
      ],
      sections: [
        {
          title: "Soporte técnico",
          body: (
            <p>
              Los problemas de acceso, errores en la aplicación, fallos al cargar
              estudios, bloqueos o cualquier comportamiento inesperado pueden
              reportarse a {emailLink(SUPPORT_EMAIL)}. Incluya la mayor cantidad de
              detalles posible, como el dispositivo utilizado, la versión de la app y
              los pasos para reproducir el problema.
            </p>
          ),
        },
        {
          title: "Cuenta y acceso profesional",
          body: (
            <p>
              Los usuarios con dudas sobre acceso a la cuenta, periodo de prueba,
              suscripción o restauración de compras pueden escribir a{" "}
              {emailLink(SUPPORT_EMAIL)} e indicar el correo registrado y una
              descripción del problema. Los profesionales invitados a pruebas de
              validación también pueden solicitar ayuda por este mismo canal.
            </p>
          ),
        },
        {
          title: "Suscripciones, planes y pagos",
          body: (
            <p>
              Las suscripciones de OrthoPrecision se gestionan según la plataforma en la
              que fueron contratadas. Las suscripciones realizadas en la app de iPhone o
              iPad son procesadas por Apple y pueden administrarse desde los ajustes de
              suscripciones del Apple ID. Las suscripciones contratadas en la web son
              gestionadas por el proveedor de pago usado en el momento de la compra.
              Para dudas sobre cobros, renovaciones, cambios de plan, restauración de
              compras o cancelación, escriba a {emailLink(SUPPORT_EMAIL)} e indique el
              correo asociado a su cuenta.
            </p>
          ),
        },
        {
          title: "Eliminación de cuenta y datos",
          body: (
            <p>
              Puede eliminar permanentemente su cuenta y todos los datos asociados
              directamente en la app, en{" "}
              <strong className="text-white">Cuenta &gt; Eliminar mi cuenta</strong>.
              Esta acción elimina de forma irreversible sus datos clínicos, archivos
              cargados y su cuenta de autenticación. Si lo prefiere, también puede
              solicitar la eliminación por {emailLink(SUPPORT_EMAIL)}.
            </p>
          ),
        },
        {
          title: "Políticas",
          body: (
            <p>
              Consulte nuestra{" "}
              <a href={canonicalLinks.privacy} className="text-cyan-300 hover:text-cyan-200">
                Política de Privacidad
              </a>{" "}
              y nuestros{" "}
              <a href={canonicalLinks.terms} className="text-cyan-300 hover:text-cyan-200">
                Términos de Uso
              </a>{" "}
              para obtener más información sobre cómo tratamos sus datos y las
              condiciones de uso de OrthoPrecision.
            </p>
          ),
        },
      ],
      footerLinks: [
        { label: "Soporte", href: canonicalLinks.support },
        { label: "Términos de Uso", href: canonicalLinks.terms },
        { label: "Política de Privacidad", href: canonicalLinks.privacy },
      ],
    },
  };

  const terms: Record<Language, PublicPageCopy> = {
    pt: {
      seoTitle: "Termos de Uso - OrthoPrecision",
      seoDescription:
        "Termos de uso completos do OrthoPrecision, com regras de acesso, responsabilidades e limitações.",
      title: "Termos de Uso - OrthoPrecision",
      updatedAtLabel: "Vigente a partir de",
      updatedAt: EFFECTIVE_DATE,
      intro: [
        <p key="intro">
          O OrthoPrecision é uma ferramenta de apoio clínico desenvolvida para auxiliar
          ortopedistas e traumatologistas na organização de pacientes, análise de
          laudos, planejamento cirúrgico, consulta de diretrizes, documentação
          clínica e gestão do fluxo de atendimento. O OrthoPrecision{" "}
          <strong className="text-white">
            não substitui avaliação médica, julgamento profissional, diretrizes
            oficiais, consulta presencial ou responsabilidade do profissional de saúde
          </strong>
          .
        </p>,
      ],
      sections: [
        {
          title: "1. Aceitação dos termos",
          body: (
            <p>
              Ao criar uma conta ou utilizar o OrthoPrecision (aplicativo móvel e
              sistema web), você concorda com estes Termos de Uso e com nossa{" "}
              <a href={canonicalLinks.privacy} className="text-cyan-300 hover:text-cyan-200">
                Política de Privacidade
              </a>
              . Caso não concorde com algum termo, não utilize o serviço.
            </p>
          ),
        },
        {
          title: "2. Sobre o serviço",
          body: (
            <p>
              O OrthoPrecision é uma ferramenta de apoio clínico destinada a
              profissionais de saúde, voltada à organização de pacientes, análise de
              laudos, planejamento cirúrgico, consulta de diretrizes e documentação
              clínica. O OrthoPrecision <strong className="text-white">não substitui
              avaliação médica, julgamento profissional, diretrizes oficiais, consulta
              presencial ou responsabilidade do profissional de saúde</strong>. Todas
              as decisões clínicas são de responsabilidade exclusiva do profissional
              usuário.
            </p>
          ),
        },
        {
          title: "3. Fase de validação profissional",
          body: (
            <p>
              O OrthoPrecision encontra-se em fase inicial de validação profissional. O
              acesso completo às funcionalidades pode estar limitado a contas
              autorizadas. Profissionais interessados em participar podem solicitar
              acesso pelo e-mail {emailLink(SUPPORT_EMAIL)}.
            </p>
          ),
        },
        {
          title: "4. Conta de usuário",
          body: (
            <p>
              Você é responsável por manter a confidencialidade das credenciais da sua
              conta e por todas as atividades realizadas nela. Informe-nos imediatamente
              em caso de uso não autorizado da sua conta.
            </p>
          ),
        },
        {
          title: "5. Dados de pacientes e responsabilidade profissional",
          body: (
            <p>
              Ao inserir dados clínicos de pacientes no OrthoPrecision, você declara
              possuir autorização, base legal e legitimidade profissional para fazê-lo,
              conforme as leis e normas éticas aplicáveis à sua jurisdição. O
              OrthoPrecision não se responsabiliza pela exatidão, adequação ou
              suficiência dos dados inseridos pelo usuário, nem por decisões clínicas
              tomadas com base em informações geradas pelo sistema.
            </p>
          ),
        },
        {
          title: "6. Assinaturas e pagamentos",
          body: (
            <p>
              Planos pagos, preços e cobranças são gerenciados exclusivamente pelo
              ambiente web do OrthoPrecision, por meio do Stripe. O aplicativo iOS não
              processa pagamentos, exibe preços ou oferece compras dentro do app. Para
              informações sobre assinaturas, acesse{" "}
              <a href="https://orthoprecision.app" className="text-cyan-300 hover:text-cyan-200">
                orthoprecision.app
              </a>
              .
            </p>
          ),
        },
        {
          title: "7. Uso aceitável",
          body: (
            <>
              <p>Ao utilizar o OrthoPrecision, você se compromete a não:</p>
              <ul className="mt-3 list-disc space-y-1.5 pl-5 text-slate-300">
                <li>utilizar o serviço para fins ilegais ou não autorizados;</li>
                <li>
                  tentar acessar dados de outros usuários ou contornar mecanismos de
                  segurança;
                </li>
                <li>
                  inserir dados de pacientes sem a devida autorização ou base legal;
                </li>
                <li>
                  utilizar o serviço de forma que comprometa sua disponibilidade,
                  integridade ou segurança.
                </li>
              </ul>
            </>
          ),
        },
        {
          title: "8. Propriedade intelectual",
          body: (
            <p>
              O OrthoPrecision, incluindo seu nome, marca, design, código-fonte e
              conteúdo, é de propriedade do OrthoPrecision e protegido por leis de
              propriedade intelectual. Nenhuma destas disposições concede ao usuário
              direitos sobre o software além do uso licenciado do serviço.
            </p>
          ),
        },
        {
          title: "9. Exclusão de conta",
          body: (
            <p>
              Você pode excluir permanentemente sua conta e os dados associados a
              qualquer momento, diretamente pelo aplicativo, em{" "}
              <strong className="text-white">Conta &gt; Excluir minha conta</strong>, ou
              solicitando pelo e-mail {emailLink(SUPPORT_EMAIL)}. Essa ação é
              irreversível.
            </p>
          ),
        },
        {
          title: "10. Limitação de responsabilidade",
          body: (
            <p>
              O OrthoPrecision é fornecido "como está", sem garantias de qualquer tipo.
              Na máxima extensão permitida pela legislação aplicável, o OrthoPrecision
              não será responsável por danos diretos, indiretos, incidentais ou
              consequenciais decorrentes do uso ou da impossibilidade de uso do
              serviço, incluindo decisões clínicas tomadas com base em informações
              geradas ou organizadas pelo sistema.
            </p>
          ),
        },
        {
          title: "11. Alterações nestes termos",
          body: (
            <p>
              Podemos atualizar estes Termos de Uso periodicamente. A versão mais
              recente estará sempre disponível em:{" "}
              <a href="https://orthoprecision.app/terms" className="text-cyan-300 hover:text-cyan-200">
                https://orthoprecision.app/terms
              </a>
              .
            </p>
          ),
        },
        {
          title: "12. Contato",
          body: (
            <p>
              Para dúvidas sobre estes Termos de Uso, entre em contato:{" "}
              {emailLink(SUPPORT_EMAIL)}.
            </p>
          ),
        },
      ],
      footerLinks: [
        { label: "Suporte", href: canonicalLinks.support },
        { label: "Termos de Uso", href: canonicalLinks.terms },
        { label: "Política de Privacidade", href: canonicalLinks.privacy },
      ],
    },
    en: {
      seoTitle: "Terms of Use - OrthoPrecision",
      seoDescription:
        "Complete Terms of Use for OrthoPrecision, covering access, responsibilities and limitations.",
      title: "Terms of Use - OrthoPrecision",
      updatedAtLabel: "Effective from",
      updatedAt: EFFECTIVE_DATE,
      intro: [
        <p key="intro">
          OrthoPrecision is a clinical support tool designed to help orthopedists and
          traumatologists organize patients, review reports, plan surgery, consult
          guidelines, document care and manage the care workflow. OrthoPrecision{" "}
          <strong className="text-white">
            does not replace medical assessment, professional judgment, official
            guidelines, in-person consultation or the responsibility of the healthcare
            professional
          </strong>
          .
        </p>,
      ],
      sections: [
        {
          title: "1. Acceptance of the terms",
          body: (
            <p>
              By creating an account or using OrthoPrecision (mobile app and web
              system), you agree to these Terms of Use and to our{" "}
              <a href={canonicalLinks.privacy} className="text-cyan-300 hover:text-cyan-200">
                Privacy Policy
              </a>
              . If you do not agree with any term, do not use the service.
            </p>
          ),
        },
        {
          title: "2. About the service",
          body: (
            <p>
              OrthoPrecision is a clinical support tool intended for healthcare
              professionals, focused on patient organization, report analysis, surgical
              planning, guideline lookup and clinical documentation. OrthoPrecision{" "}
              <strong className="text-white">does not replace medical assessment,
              professional judgment, official guidelines, in-person consultation or the
              responsibility of the healthcare professional</strong>. All clinical
              decisions remain the sole responsibility of the user.
            </p>
          ),
        },
        {
          title: "3. Professional validation phase",
          body: (
            <p>
              OrthoPrecision is in an initial professional validation phase. Full access
              to features may be limited to authorized accounts. Professionals who wish
              to participate can request access by email at {emailLink(SUPPORT_EMAIL)}.
            </p>
          ),
        },
        {
          title: "4. User account",
          body: (
            <p>
              You are responsible for keeping your account credentials confidential and
              for all activity performed under your account. Please notify us
              immediately in case of unauthorized use of your account.
            </p>
          ),
        },
        {
          title: "5. Patient data and professional responsibility",
          body: (
            <p>
              When entering patient clinical data into OrthoPrecision, you declare that
              you have the authorization, legal basis and professional legitimacy to do
              so, in accordance with the laws and ethical rules applicable in your
              jurisdiction. OrthoPrecision is not responsible for the accuracy,
              adequacy or sufficiency of the data entered by the user, nor for clinical
              decisions made based on information generated by the system.
            </p>
          ),
        },
        {
          title: "6. Subscriptions and payments",
          body: (
            <p>
              Paid plans, pricing and billing are managed exclusively through the
              OrthoPrecision web environment, via Stripe. The iOS app does not process
              payments, display prices or offer in-app purchases. For subscription
              information, visit{" "}
              <a href="https://orthoprecision.app" className="text-cyan-300 hover:text-cyan-200">
                orthoprecision.app
              </a>
              .
            </p>
          ),
        },
        {
          title: "7. Acceptable use",
          body: (
            <>
              <p>By using OrthoPrecision, you agree not to:</p>
              <ul className="mt-3 list-disc space-y-1.5 pl-5 text-slate-300">
                <li>use the service for illegal or unauthorized purposes;</li>
                <li>
                  attempt to access other users' data or bypass security mechanisms;
                </li>
                <li>
                  enter patient data without the proper authorization or legal basis;
                </li>
                <li>
                  use the service in a way that compromises its availability, integrity
                  or security.
                </li>
              </ul>
            </>
          ),
        },
        {
          title: "8. Intellectual property",
          body: (
            <p>
              OrthoPrecision, including its name, brand, design, source code and
              content, is owned by OrthoPrecision and protected by intellectual
              property laws. Nothing in these terms grants the user any rights over the
              software beyond the licensed use of the service.
            </p>
          ),
        },
        {
          title: "9. Account deletion",
          body: (
            <p>
              You may permanently delete your account and associated data at any time,
              directly in the app at{" "}
              <strong className="text-white">Account &gt; Delete my account</strong>,
              or by emailing {emailLink(SUPPORT_EMAIL)}. This action is irreversible.
            </p>
          ),
        },
        {
          title: "10. Limitation of liability",
          body: (
            <p>
              OrthoPrecision is provided "as is", without warranties of any kind. To
              the maximum extent permitted by applicable law, OrthoPrecision shall not
              be liable for any direct, indirect, incidental or consequential damages
              arising from the use or inability to use the service, including clinical
              decisions made based on information generated or organized by the system.
            </p>
          ),
        },
        {
          title: "11. Changes to these terms",
          body: (
            <p>
              We may update these Terms of Use from time to time. The latest version
              will always be available at:{" "}
              <a href="https://orthoprecision.app/terms" className="text-cyan-300 hover:text-cyan-200">
                https://orthoprecision.app/terms
              </a>
              .
            </p>
          ),
        },
        {
          title: "12. Contact",
          body: (
            <p>
              If you have questions about these Terms of Use, contact us at{" "}
              {emailLink(SUPPORT_EMAIL)}.
            </p>
          ),
        },
      ],
      footerLinks: [
        { label: "Support", href: canonicalLinks.support },
        { label: "Terms of Use", href: canonicalLinks.terms },
        { label: "Privacy Policy", href: canonicalLinks.privacy },
      ],
    },
    es: {
      seoTitle: "Términos de Uso - OrthoPrecision",
      seoDescription:
        "Términos de uso completos de OrthoPrecision, con reglas de acceso, responsabilidades y limitaciones.",
      title: "Términos de Uso - OrthoPrecision",
      updatedAtLabel: "Vigente a partir de",
      updatedAt: EFFECTIVE_DATE,
      intro: [
        <p key="intro">
          OrthoPrecision es una herramienta de apoyo clínico desarrollada para ayudar a
          ortopedistas y traumatólogos en la organización de pacientes, análisis de
          informes, planificación quirúrgica, consulta de directrices, documentación
          clínica y gestión del flujo de atención. OrthoPrecision{" "}
          <strong className="text-white">
            no sustituye la evaluación médica, el juicio profesional, las directrices
            oficiales, la consulta presencial ni la responsabilidad del profesional de
            la salud
          </strong>
          .
        </p>,
      ],
      sections: [
        {
          title: "1. Aceptación de los términos",
          body: (
            <p>
              Al crear una cuenta o utilizar OrthoPrecision (aplicación móvil y sistema
              web), usted acepta estos Términos de Uso y nuestra{" "}
              <a href={canonicalLinks.privacy} className="text-cyan-300 hover:text-cyan-200">
                Política de Privacidad
              </a>
              . Si no está de acuerdo con algún término, no utilice el servicio.
            </p>
          ),
        },
        {
          title: "2. Sobre el servicio",
          body: (
            <p>
              OrthoPrecision es una herramienta de apoyo clínico destinada a
              profesionales de la salud, orientada a la organización de pacientes,
              análisis de informes, planificación quirúrgica, consulta de directrices y
              documentación clínica. OrthoPrecision{" "}
              <strong className="text-white">no sustituye la evaluación médica, el
              juicio profesional, las directrices oficiales, la consulta presencial ni
              la responsabilidad del profesional de la salud</strong>. Todas las
              decisiones clínicas son responsabilidad exclusiva del profesional usuario.
            </p>
          ),
        },
        {
          title: "3. Fase de validación profesional",
          body: (
            <p>
              OrthoPrecision se encuentra en una fase inicial de validación profesional.
              El acceso completo a las funciones puede estar limitado a cuentas
              autorizadas. Los profesionales interesados en participar pueden solicitar
              acceso por correo electrónico a {emailLink(SUPPORT_EMAIL)}.
            </p>
          ),
        },
        {
          title: "4. Cuenta de usuario",
          body: (
            <p>
              Usted es responsable de mantener la confidencialidad de las credenciales
              de su cuenta y de todas las actividades realizadas en ella. Avísenos de
              inmediato en caso de uso no autorizado de su cuenta.
            </p>
          ),
        },
        {
          title: "5. Datos de pacientes y responsabilidad profesional",
          body: (
            <p>
              Al ingresar datos clínicos de pacientes en OrthoPrecision, usted declara
              que cuenta con la autorización, la base legal y la legitimidad
              profesional para hacerlo, de acuerdo con las leyes y normas éticas
              aplicables en su jurisdicción. OrthoPrecision no se responsabiliza por la
              exactitud, adecuación o suficiencia de los datos ingresados por el
              usuario, ni por decisiones clínicas tomadas con base en información
              generada por el sistema.
            </p>
          ),
        },
        {
          title: "6. Suscripciones y pagos",
          body: (
            <p>
              Los planes pagos, precios y cobros se gestionan exclusivamente en el
              entorno web de OrthoPrecision, mediante Stripe. La aplicación iOS no
              procesa pagos, no muestra precios ni ofrece compras dentro de la app. Para
              información sobre suscripciones, visite{" "}
              <a href="https://orthoprecision.app" className="text-cyan-300 hover:text-cyan-200">
                orthoprecision.app
              </a>
              .
            </p>
          ),
        },
        {
          title: "7. Uso aceptable",
          body: (
            <>
              <p>Al utilizar OrthoPrecision, usted se compromete a no:</p>
              <ul className="mt-3 list-disc space-y-1.5 pl-5 text-slate-300">
                <li>utilizar el servicio con fines ilegales o no autorizados;</li>
                <li>
                  intentar acceder a datos de otros usuarios o eludir mecanismos de
                  seguridad;
                </li>
                <li>
                  ingresar datos de pacientes sin la autorización o base legal
                  correspondiente;
                </li>
                <li>
                  usar el servicio de forma que comprometa su disponibilidad, integridad
                  o seguridad.
                </li>
              </ul>
            </>
          ),
        },
        {
          title: "8. Propiedad intelectual",
          body: (
            <p>
              OrthoPrecision, incluyendo su nombre, marca, diseño, código fuente y
              contenido, es propiedad de OrthoPrecision y está protegido por las leyes
              de propiedad intelectual. Ninguna disposición de estos términos concede al
              usuario derechos sobre el software más allá del uso licenciado del
              servicio.
            </p>
          ),
        },
        {
          title: "9. Eliminación de cuenta",
          body: (
            <p>
              Puede eliminar permanentemente su cuenta y los datos asociados en
              cualquier momento, directamente en la app, en{" "}
              <strong className="text-white">Cuenta &gt; Eliminar mi cuenta</strong>, o
              escribiendo a {emailLink(SUPPORT_EMAIL)}. Esta acción es irreversible.
            </p>
          ),
        },
        {
          title: "10. Limitación de responsabilidad",
          body: (
            <p>
              OrthoPrecision se proporciona "tal cual", sin garantías de ningún tipo.
              En la máxima medida permitida por la ley aplicable, OrthoPrecision no será
              responsable por daños directos, indirectos, incidentales o consecuentes
              derivados del uso o de la imposibilidad de usar el servicio, incluidas las
              decisiones clínicas tomadas con base en información generada u organizada
              por el sistema.
            </p>
          ),
        },
        {
          title: "11. Cambios en estos términos",
          body: (
            <p>
              Podemos actualizar estos Términos de Uso periódicamente. La versión más
              reciente siempre estará disponible en:{" "}
              <a href="https://orthoprecision.app/terms" className="text-cyan-300 hover:text-cyan-200">
                https://orthoprecision.app/terms
              </a>
              .
            </p>
          ),
        },
        {
          title: "12. Contacto",
          body: (
            <p>
              Si tiene preguntas sobre estos Términos de Uso, contáctenos en{" "}
              {emailLink(SUPPORT_EMAIL)}.
            </p>
          ),
        },
      ],
      footerLinks: [
        { label: "Soporte", href: canonicalLinks.support },
        { label: "Términos de Uso", href: canonicalLinks.terms },
        { label: "Política de Privacidad", href: canonicalLinks.privacy },
      ],
    },
  };

  const privacy: Record<Language, PublicPageCopy> = {
    pt: {
      seoTitle: "Política de Privacidade - OrthoPrecision",
      seoDescription:
        "Política de privacidade completa do OrthoPrecision, com detalhes sobre coleta, uso, retenção e proteção de dados.",
      title: "Política de Privacidade - OrthoPrecision",
      updatedAtLabel: "Vigente a partir de",
      updatedAt: EFFECTIVE_DATE,
      intro: [
        <p key="intro">
          Esta Política de Privacidade explica como o OrthoPrecision coleta, usa,
          armazena e protege informações quando você utiliza nosso aplicativo, sistema
          web e serviços relacionados.
        </p>,
        <p key="intro-2">
          O OrthoPrecision é uma ferramenta de apoio clínico desenvolvida para auxiliar
          ortopedistas e traumatologistas na organização de pacientes, análise de
          laudos, planejamento cirúrgico, consulta de diretrizes, documentação
          clínica e gestão do fluxo de atendimento. O OrthoPrecision{" "}
          <strong className="text-white">
            não substitui avaliação médica, julgamento profissional, diretrizes
            oficiais, consulta presencial ou responsabilidade do profissional de saúde
          </strong>
          .
        </p>,
      ],
      sections: [
        {
          title: "1. Quem somos",
          body: (
            <p>
              O OrthoPrecision é operado por OrthoPrecision. Para assuntos relacionados
              à privacidade, entre em contato pelo e-mail:{" "}
              {emailLink(PRIVACY_EMAIL)}.
            </p>
          ),
        },
        {
          title: "2. Dados que coletamos",
          body: (
            <p>
              Podemos coletar e processar os seguintes tipos de dados, conforme o uso do
              aplicativo:
            </p>
          ),
        },
        {
          title: "2.1 Dados de conta e identificação",
          body: (
            <p>
              Podemos coletar nome, endereço de e-mail, identificador de usuário, dados
              de autenticação e informações necessárias para criação e manutenção da
              conta.
            </p>
          ),
        },
        {
          title: "2.2 Dados profissionais",
          body: (
            <p>
              Podemos coletar informações profissionais fornecidas pelo usuário, como
              CRM, RQE, especialidade, subespecialidade, instituição, hospital, clínica,
              telefone profissional e dados usados para identificação profissional no
              sistema.
            </p>
          ),
        },
        {
          title: "2.3 Dados clínicos inseridos pelo usuário",
          body: (
            <p>
              O usuário pode inserir informações clínicas, histórico, anamnese,
              descrições de casos, observações, dados de pacientes, registros clínicos,
              condutas, laudos e informações relacionadas ao atendimento ortopédico.
            </p>
          ),
        },
        {
          title: "2.4 Arquivos, imagens e documentos enviados",
          body: (
            <p>
              O usuário pode enviar imagens, exames, PDFs, documentos, fotografias
              clínicas ou arquivos relacionados a casos clínicos. Esses arquivos podem
              ser armazenados e processados para permitir funcionalidades como análise
              de laudos, planejamento cirúrgico e organização do caso.
            </p>
          ),
        },
        {
          title: "2.5 Dados de áudio e transcrição, quando usados",
          body: (
            <p>
              Quando o usuário utiliza recursos de voz ou ditado clínico, podemos
              processar áudio ou transcrições para converter fala em texto, preencher
              registros clínicos ou auxiliar na documentação.
            </p>
          ),
        },
        {
          title: "2.6 Dados de agenda / Google Calendar, quando conectado pelo usuário",
          body: (
            <p>
              Quando o usuário conecta o Google Calendar, podemos acessar, exibir ou
              sincronizar eventos de agenda conforme autorização concedida pelo próprio
              usuário. O usuário pode desconectar essa integração quando desejar.
            </p>
          ),
        },
        {
          title: "2.7 Dados de suporte",
          body: (
            <p>
              Podemos coletar mensagens de suporte, feedback, checklist de teste,
              descrições de problemas e informações necessárias para responder
              solicitações do usuário.
            </p>
          ),
        },
        {
          title: "2.8 Dados técnicos e de uso",
          body: (
            <p>
              Podemos coletar dados técnicos necessários ao funcionamento, segurança e
              melhoria do serviço, como registros de acesso, tipo de dispositivo,
              versão do app, eventos técnicos, erros, uso de funcionalidades, limites de
              uso e status do plano.
            </p>
          ),
        },
        {
          title: "3. Como usamos os dados",
          body: (
            <>
              <p>Usamos os dados para:</p>
              <ul className="mt-3 list-disc space-y-1.5 pl-5 text-slate-300">
                <li>criar e autenticar contas;</li>
                <li>operar funcionalidades do app e do sistema web;</li>
                <li>organizar pacientes, casos e documentos;</li>
                <li>
                  processar solicitações de análise clínica, laudos, diretrizes e
                  planejamento;
                </li>
                <li>sincronizar agenda quando autorizado;</li>
                <li>prestar suporte ao usuário;</li>
                <li>manter segurança, prevenção de abuso e integridade do serviço;</li>
                <li>controlar planos, limites de uso e permissões;</li>
                <li>melhorar estabilidade, desempenho e experiência do usuário.</li>
              </ul>
            </>
          ),
        },
        {
          title: "4. Serviços de terceiros utilizados",
          body: (
            <p>
              O OrthoPrecision pode utilizar serviços de terceiros para operar o
              produto, incluindo os descritos a seguir.
            </p>
          ),
        },
        {
          title: "4.1 Firebase / Google Cloud",
          body: (
            <p>
              Usados para autenticação, banco de dados, armazenamento de arquivos,
              funções de backend, hospedagem e infraestrutura.
            </p>
          ),
        },
        {
          title: "4.2 Stripe no ambiente web",
          body: (
            <p>
              Usado no ambiente web para processamento de pagamentos, assinaturas e
              gestão de cobrança. O app iOS não processa pagamentos diretamente dentro
              do aplicativo.
            </p>
          ),
        },
        {
          title: "4.3 Google Calendar, quando conectado",
          body: (
            <p>
              Usado apenas quando o usuário conecta voluntariamente sua conta Google
              para integração de agenda.
            </p>
          ),
        },
        {
          title: "4.4 Serviços de IA para processamento clínico",
          body: (
            <p>
              Podemos usar serviços de inteligência artificial para processar textos,
              imagens, laudos, diretrizes e solicitações clínicas enviadas pelo usuário.
              As respostas geradas por IA devem ser revisadas por profissional
              habilitado antes de qualquer uso clínico.
            </p>
          ),
        },
        {
          title: "5. Compartilhamento de dados",
          body: (
            <>
              <p>
                Não vendemos dados pessoais. Podemos compartilhar dados apenas quando
                necessário para:
              </p>
              <ul className="mt-3 list-disc space-y-1.5 pl-5 text-slate-300">
                <li>
                  operar serviços contratados de infraestrutura, autenticação,
                  armazenamento, pagamento, agenda ou IA;
                </li>
                <li>cumprir obrigações legais;</li>
                <li>
                  proteger direitos, segurança e integridade do OrthoPrecision,
                  usuários ou terceiros;
                </li>
                <li>executar funcionalidades solicitadas pelo usuário.</li>
              </ul>
              <p className="mt-3">
                O OrthoPrecision não utiliza os dados para rastreamento publicitário
                entre aplicativos e sites de terceiros.
              </p>
            </>
          ),
        },
        {
          title: "6. Segurança",
          body: (
            <p>
              Adotamos medidas técnicas e organizacionais para proteger os dados,
              incluindo autenticação, controles de acesso, armazenamento em serviços de
              nuvem confiáveis e práticas de segurança compatíveis com o funcionamento
              do produto. No entanto, nenhum sistema é totalmente imune a riscos.
            </p>
          ),
        },
        {
          title: "7. Retenção e exclusão de dados",
          body: (
            <>
              <p>
                Mantemos os dados enquanto forem necessários para oferecer o serviço,
                cumprir obrigações legais, resolver disputas, prevenir abusos ou
                conforme solicitado pelo usuário.
              </p>
              <p className="mt-3">
                O usuário pode solicitar exclusão, correção ou atualização de dados
                entrando em contato pelo e-mail: {emailLink(PRIVACY_EMAIL)}.
              </p>
            </>
          ),
        },
        {
          title: "8. Direitos do usuário",
          body: (
            <p>
              Dependendo da legislação aplicável, o usuário pode ter direito de
              acessar, corrigir, excluir, portar ou restringir o uso de seus dados.
              Solicitações podem ser enviadas para: {emailLink(PRIVACY_EMAIL)}.
            </p>
          ),
        },
        {
          title: "9. Privacidade de pacientes",
          body: (
            <>
              <p>
                O OrthoPrecision pode conter informações clínicas de pacientes inseridas
                pelo profissional usuário. O usuário é responsável por garantir que
                possui autorização, base legal ou legitimidade profissional para
                inserir, processar e armazenar dados de pacientes no sistema, conforme
                leis e normas aplicáveis.
              </p>
              <p className="mt-3">
                Recomendamos evitar inserir dados desnecessários ou excessivos e
                utilizar o sistema de forma compatível com obrigações profissionais,
                éticas e legais.
              </p>
            </>
          ),
        },
        {
          title: "10. Transferência internacional de dados",
          body: (
            <p>
              Os dados podem ser processados e armazenados em servidores localizados em
              diferentes países, de acordo com a infraestrutura dos provedores
              utilizados pelo OrthoPrecision.
            </p>
          ),
        },
        {
          title: "11. Crianças e menores",
          body: (
            <p>
              O OrthoPrecision é destinado a profissionais de saúde e não é
              direcionado a crianças. O app não deve ser utilizado por menores de
              idade.
            </p>
          ),
        },
        {
          title: "12. Alterações nesta política",
          body: (
            <p>
              Podemos atualizar esta Política de Privacidade periodicamente. A versão
              mais recente estará sempre disponível em:{" "}
              <a href="https://orthoprecision.app/privacy" className="text-cyan-300 hover:text-cyan-200">
                https://orthoprecision.app/privacy
              </a>
              .
            </p>
          ),
        },
        {
          title: "13. Contato",
          body: (
            <p>
              Para dúvidas, solicitações ou questões relacionadas à privacidade, entre
              em contato: {emailLink(PRIVACY_EMAIL)}.
            </p>
          ),
        },
      ],
      footerLinks: [
        { label: "Suporte", href: canonicalLinks.support },
        { label: "Termos de Uso", href: canonicalLinks.terms },
        { label: "Política de Privacidade", href: canonicalLinks.privacy },
      ],
    },
    en: {
      seoTitle: "Privacy Policy - OrthoPrecision",
      seoDescription:
        "Complete privacy policy for OrthoPrecision, including data collection, use, retention and protection details.",
      title: "Privacy Policy - OrthoPrecision",
      updatedAtLabel: "Effective from",
      updatedAt: EFFECTIVE_DATE,
      intro: [
        <p key="intro">
          This Privacy Policy explains how OrthoPrecision collects, uses, stores and
          protects information when you use our app, web system and related services.
        </p>,
        <p key="intro-2">
          OrthoPrecision is a clinical support tool designed to help orthopedists and
          traumatologists organize patients, review reports, plan surgery, consult
          guidelines, document care and manage the care workflow. OrthoPrecision{" "}
          <strong className="text-white">
            does not replace medical assessment, professional judgment, official
            guidelines, in-person consultation or the responsibility of the healthcare
            professional
          </strong>
          .
        </p>,
      ],
      sections: [
        {
          title: "1. Who we are",
          body: (
            <p>
              OrthoPrecision is operated by OrthoPrecision. For privacy-related matters,
              contact us at: {emailLink(PRIVACY_EMAIL)}.
            </p>
          ),
        },
        {
          title: "2. Data we collect",
          body: (
            <p>
              We may collect and process the following types of data, depending on how
              the app is used:
            </p>
          ),
        },
        {
          title: "2.1 Account and identification data",
          body: (
            <p>
              We may collect name, email address, user identifier, authentication data
              and information needed to create and maintain the account.
            </p>
          ),
        },
        {
          title: "2.2 Professional data",
          body: (
            <p>
              We may collect professional information provided by the user, such as
              CRM, RQE, specialty, subspecialty, institution, hospital, clinic,
              professional phone number and data used for professional identification in
              the system.
            </p>
          ),
        },
        {
          title: "2.3 Clinical data entered by the user",
          body: (
            <p>
              The user may enter clinical information, history, anamnesis, case
              descriptions, notes, patient data, clinical records, care plans, reports
              and information related to orthopedic care.
            </p>
          ),
        },
        {
          title: "2.4 Uploaded files, images and documents",
          body: (
            <p>
              The user may upload images, exams, PDFs, documents, clinical photos or
              files related to clinical cases. These files may be stored and processed
              to enable features such as report analysis, surgical planning and case
              organization.
            </p>
          ),
        },
        {
          title: "2.5 Audio and transcription data, when used",
          body: (
            <p>
              When the user uses voice or clinical dictation features, we may process
              audio or transcriptions to convert speech to text, populate clinical
              records or assist in documentation.
            </p>
          ),
        },
        {
          title: "2.6 Calendar / Google Calendar data, when connected by the user",
          body: (
            <p>
              When the user connects Google Calendar, we may access, display or sync
              calendar events according to the authorization granted by the user. The
              user can disconnect this integration at any time.
            </p>
          ),
        },
        {
          title: "2.7 Support data",
          body: (
            <p>
              We may collect support messages, feedback, test checklists, problem
              descriptions and information needed to respond to user requests.
            </p>
          ),
        },
        {
          title: "2.8 Technical and usage data",
          body: (
            <p>
              We may collect technical data necessary for the operation, security and
              improvement of the service, such as access logs, device type, app
              version, technical events, errors, feature usage, usage limits and plan
              status.
            </p>
          ),
        },
        {
          title: "3. How we use data",
          body: (
            <>
              <p>We use data to:</p>
              <ul className="mt-3 list-disc space-y-1.5 pl-5 text-slate-300">
                <li>create and authenticate accounts;</li>
                <li>operate app and web features;</li>
                <li>organize patients, cases and documents;</li>
                <li>
                  process clinical analysis requests, reports, guidelines and planning;
                </li>
                <li>sync calendars when authorized;</li>
                <li>provide user support;</li>
                <li>maintain security, abuse prevention and service integrity;</li>
                <li>manage plans, usage limits and permissions;</li>
                <li>improve stability, performance and the user experience.</li>
              </ul>
            </>
          ),
        },
        {
          title: "4. Third-party services used",
          body: (
            <p>
              OrthoPrecision may use third-party services to operate the product,
              including those described below.
            </p>
          ),
        },
        {
          title: "4.1 Firebase / Google Cloud",
          body: (
            <p>
              Used for authentication, database, file storage, backend functions,
              hosting and infrastructure.
            </p>
          ),
        },
        {
          title: "4.2 Stripe in the web environment",
          body: (
            <p>
              Used in the web environment for payment processing, subscriptions and
              billing management. The iOS app does not process payments directly inside
              the app.
            </p>
          ),
        },
        {
          title: "4.3 Google Calendar, when connected",
          body: (
            <p>
              Used only when the user voluntarily connects their Google account for
              calendar integration.
            </p>
          ),
        },
        {
          title: "4.4 AI services for clinical processing",
          body: (
            <p>
              We may use artificial intelligence services to process text, images,
              reports, guidelines and clinical requests submitted by the user. AI
              outputs must be reviewed by a licensed professional before any clinical
              use.
            </p>
          ),
        },
        {
          title: "5. Data sharing",
          body: (
            <>
              <p>
                We do not sell personal data. We may share data only when necessary to:
              </p>
              <ul className="mt-3 list-disc space-y-1.5 pl-5 text-slate-300">
                <li>
                  operate contracted infrastructure, authentication, storage, payment,
                  calendar or AI services;
                </li>
                <li>comply with legal obligations;</li>
                <li>
                  protect the rights, security and integrity of OrthoPrecision, users or
                  third parties;
                </li>
                <li>execute functionality requested by the user.</li>
              </ul>
              <p className="mt-3">
                OrthoPrecision does not use data for cross-app or cross-site advertising
                tracking.
              </p>
            </>
          ),
        },
        {
          title: "6. Security",
          body: (
            <p>
              We adopt technical and organizational measures to protect data, including
              authentication, access controls, storage in reliable cloud services and
              security practices appropriate for the product. However, no system is
              completely immune to risk.
            </p>
          ),
        },
        {
          title: "7. Data retention and deletion",
          body: (
            <>
              <p>
                We keep data as long as it is needed to provide the service, comply
                with legal obligations, resolve disputes, prevent abuse or as requested
                by the user.
              </p>
              <p className="mt-3">
                The user may request deletion, correction or update of data by contacting
                us at: {emailLink(PRIVACY_EMAIL)}.
              </p>
            </>
          ),
        },
        {
          title: "8. User rights",
          body: (
            <p>
              Depending on the applicable law, the user may have the right to access,
              correct, delete, port or restrict the use of their data. Requests can be
              sent to: {emailLink(PRIVACY_EMAIL)}.
            </p>
          ),
        },
        {
          title: "9. Patient privacy",
          body: (
            <>
              <p>
                OrthoPrecision may contain clinical information about patients entered
                by the professional user. The user is responsible for ensuring they have
                authorization, a legal basis or professional legitimacy to enter,
                process and store patient data in the system, according to applicable
                laws and rules.
              </p>
              <p className="mt-3">
                We recommend avoiding unnecessary or excessive data entry and using the
                system in a way that is consistent with professional, ethical and legal
                obligations.
              </p>
            </>
          ),
        },
        {
          title: "10. International data transfer",
          body: (
            <p>
              Data may be processed and stored on servers located in different countries,
              according to the infrastructure of the providers used by OrthoPrecision.
            </p>
          ),
        },
        {
          title: "11. Children and minors",
          body: (
            <p>
              OrthoPrecision is intended for healthcare professionals and is not
              directed at children. The app should not be used by minors.
            </p>
          ),
        },
        {
          title: "12. Changes to this policy",
          body: (
            <p>
              We may update this Privacy Policy from time to time. The latest version
              will always be available at:{" "}
              <a href="https://orthoprecision.app/privacy" className="text-cyan-300 hover:text-cyan-200">
                https://orthoprecision.app/privacy
              </a>
              .
            </p>
          ),
        },
        {
          title: "13. Contact",
          body: (
            <p>
              If you have questions, requests or privacy-related concerns, contact us at{" "}
              {emailLink(PRIVACY_EMAIL)}.
            </p>
          ),
        },
      ],
      footerLinks: [
        { label: "Support", href: canonicalLinks.support },
        { label: "Terms of Use", href: canonicalLinks.terms },
        { label: "Privacy Policy", href: canonicalLinks.privacy },
      ],
    },
    es: {
      seoTitle: "Política de Privacidad - OrthoPrecision",
      seoDescription:
        "Política de privacidad completa de OrthoPrecision, con detalles sobre recopilación, uso, retención y protección de datos.",
      title: "Política de Privacidad - OrthoPrecision",
      updatedAtLabel: "Vigente a partir de",
      updatedAt: EFFECTIVE_DATE,
      intro: [
        <p key="intro">
          Esta Política de Privacidad explica cómo OrthoPrecision recopila, usa,
          almacena y protege la información cuando usted utiliza nuestra aplicación,
          el sistema web y los servicios relacionados.
        </p>,
        <p key="intro-2">
          OrthoPrecision es una herramienta de apoyo clínico desarrollada para ayudar a
          ortopedistas y traumatólogos en la organización de pacientes, análisis de
          informes, planificación quirúrgica, consulta de directrices, documentación
          clínica y gestión del flujo de atención. OrthoPrecision{" "}
          <strong className="text-white">
            no sustituye la evaluación médica, el juicio profesional, las directrices
            oficiales, la consulta presencial ni la responsabilidad del profesional de
            la salud
          </strong>
          .
        </p>,
      ],
      sections: [
        {
          title: "1. Quiénes somos",
          body: (
            <p>
              OrthoPrecision es operado por OrthoPrecision. Para asuntos relacionados
              con la privacidad, contáctenos en: {emailLink(PRIVACY_EMAIL)}.
            </p>
          ),
        },
        {
          title: "2. Datos que recopilamos",
          body: (
            <p>
              Podemos recopilar y procesar los siguientes tipos de datos, según el uso
              de la aplicación:
            </p>
          ),
        },
        {
          title: "2.1 Datos de cuenta e identificación",
          body: (
            <p>
              Podemos recopilar nombre, dirección de correo electrónico, identificador
              de usuario, datos de autenticación e información necesaria para crear y
              mantener la cuenta.
            </p>
          ),
        },
        {
          title: "2.2 Datos profesionales",
          body: (
            <p>
              Podemos recopilar información profesional proporcionada por el usuario,
              como CRM, RQE, especialidad, subespecialidad, institución, hospital,
              clínica, teléfono profesional y datos usados para la identificación
              profesional en el sistema.
            </p>
          ),
        },
        {
          title: "2.3 Datos clínicos ingresados por el usuario",
          body: (
            <p>
              El usuario puede ingresar información clínica, historia, anamnesis,
              descripciones de casos, observaciones, datos de pacientes, registros
              clínicos, conductas, informes e información relacionada con la atención
              ortopédica.
            </p>
          ),
        },
        {
          title: "2.4 Archivos, imágenes y documentos enviados",
          body: (
            <p>
              El usuario puede enviar imágenes, estudios, PDF, documentos, fotografías
              clínicas o archivos relacionados con casos clínicos. Estos archivos pueden
              almacenarse y procesarse para permitir funciones como análisis de
              informes, planificación quirúrgica y organización del caso.
            </p>
          ),
        },
        {
          title: "2.5 Datos de audio y transcripción, cuando se usan",
          body: (
            <p>
              Cuando el usuario utiliza funciones de voz o dictado clínico, podemos
              procesar audio o transcripciones para convertir la voz en texto,
              completar registros clínicos o ayudar en la documentación.
            </p>
          ),
        },
        {
          title: "2.6 Datos de agenda / Google Calendar, cuando se conecta por el usuario",
          body: (
            <p>
              Cuando el usuario conecta Google Calendar, podemos acceder, mostrar o
              sincronizar eventos de agenda según la autorización concedida por el
              propio usuario. El usuario puede desconectar esta integración cuando lo
              desee.
            </p>
          ),
        },
        {
          title: "2.7 Datos de soporte",
          body: (
            <p>
              Podemos recopilar mensajes de soporte, comentarios, listas de verificación
              de prueba, descripciones de problemas e información necesaria para
              responder a las solicitudes del usuario.
            </p>
          ),
        },
        {
          title: "2.8 Datos técnicos y de uso",
          body: (
            <p>
              Podemos recopilar datos técnicos necesarios para el funcionamiento, la
              seguridad y la mejora del servicio, como registros de acceso, tipo de
              dispositivo, versión de la app, eventos técnicos, errores, uso de
              funciones, límites de uso y estado del plan.
            </p>
          ),
        },
        {
          title: "3. Cómo usamos los datos",
          body: (
            <>
              <p>Usamos los datos para:</p>
              <ul className="mt-3 list-disc space-y-1.5 pl-5 text-slate-300">
                <li>crear y autenticar cuentas;</li>
                <li>operar funciones de la app y del sistema web;</li>
                <li>organizar pacientes, casos y documentos;</li>
                <li>
                  procesar solicitudes de análisis clínico, informes, directrices y
                  planificación;
                </li>
                <li>sincronizar la agenda cuando esté autorizado;</li>
                <li>brindar soporte al usuario;</li>
                <li>mantener la seguridad, la prevención de abuso y la integridad del
                  servicio;</li>
                <li>controlar planes, límites de uso y permisos;</li>
                <li>mejorar la estabilidad, el rendimiento y la experiencia del usuario.</li>
              </ul>
            </>
          ),
        },
        {
          title: "4. Servicios de terceros utilizados",
          body: (
            <p>
              OrthoPrecision puede utilizar servicios de terceros para operar el
              producto, incluidos los descritos a continuación.
            </p>
          ),
        },
        {
          title: "4.1 Firebase / Google Cloud",
          body: (
            <p>
              Usados para autenticación, base de datos, almacenamiento de archivos,
              funciones de backend, alojamiento e infraestructura.
            </p>
          ),
        },
        {
          title: "4.2 Stripe en el entorno web",
          body: (
            <p>
              Usado en el entorno web para procesamiento de pagos, suscripciones y
              gestión de cobros. La app iOS no procesa pagos directamente dentro de la
              aplicación.
            </p>
          ),
        },
        {
          title: "4.3 Google Calendar, cuando se conecta",
          body: (
            <p>
              Usado solo cuando el usuario conecta voluntariamente su cuenta Google para
              la integración de agenda.
            </p>
          ),
        },
        {
          title: "4.4 Servicios de IA para procesamiento clínico",
          body: (
            <p>
              Podemos usar servicios de inteligencia artificial para procesar textos,
              imágenes, informes, directrices y solicitudes clínicas enviadas por el
              usuario. Las respuestas generadas por IA deben ser revisadas por un
              profesional habilitado antes de cualquier uso clínico.
            </p>
          ),
        },
        {
          title: "5. Compartición de datos",
          body: (
            <>
              <p>
                No vendemos datos personales. Podemos compartir datos solo cuando sea
                necesario para:
              </p>
              <ul className="mt-3 list-disc space-y-1.5 pl-5 text-slate-300">
                <li>
                  operar servicios contratados de infraestructura, autenticación,
                  almacenamiento, pago, agenda o IA;
                </li>
                <li>cumplir obligaciones legales;</li>
                <li>
                  proteger los derechos, la seguridad y la integridad de OrthoPrecision,
                  los usuarios o terceros;
                </li>
                <li>ejecutar funcionalidades solicitadas por el usuario.</li>
              </ul>
              <p className="mt-3">
                OrthoPrecision no utiliza los datos para seguimiento publicitario entre
                aplicaciones y sitios de terceros.
              </p>
            </>
          ),
        },
        {
          title: "6. Seguridad",
          body: (
            <p>
              Adoptamos medidas técnicas y organizativas para proteger los datos,
              incluidas autenticación, controles de acceso, almacenamiento en servicios
              de nube confiables y prácticas de seguridad compatibles con el
              funcionamiento del producto. Sin embargo, ningún sistema es totalmente
              inmune al riesgo.
            </p>
          ),
        },
        {
          title: "7. Conservación y eliminación de datos",
          body: (
            <>
              <p>
                Conservamos los datos mientras sean necesarios para ofrecer el servicio,
                cumplir obligaciones legales, resolver disputas, prevenir abusos o
                según lo solicite el usuario.
              </p>
              <p className="mt-3">
                El usuario puede solicitar eliminación, corrección o actualización de
                datos contactando a: {emailLink(PRIVACY_EMAIL)}.
              </p>
            </>
          ),
        },
        {
          title: "8. Derechos del usuario",
          body: (
            <p>
              Según la legislación aplicable, el usuario puede tener derecho a acceder,
              corregir, eliminar, portar o restringir el uso de sus datos. Las
              solicitudes pueden enviarse a: {emailLink(PRIVACY_EMAIL)}.
            </p>
          ),
        },
        {
          title: "9. Privacidad de pacientes",
          body: (
            <>
              <p>
                OrthoPrecision puede contener información clínica de pacientes
                ingresada por el profesional usuario. El usuario es responsable de
                garantizar que tiene autorización, base legal o legitimidad profesional
                para ingresar, procesar y almacenar datos de pacientes en el sistema,
                conforme a las leyes y normas aplicables.
              </p>
              <p className="mt-3">
                Recomendamos evitar ingresar datos innecesarios o excesivos y usar el
                sistema de forma compatible con obligaciones profesionales, éticas y
                legales.
              </p>
            </>
          ),
        },
        {
          title: "10. Transferencia internacional de datos",
          body: (
            <p>
              Los datos pueden procesarse y almacenarse en servidores ubicados en
              distintos países, según la infraestructura de los proveedores usados por
              OrthoPrecision.
            </p>
          ),
        },
        {
          title: "11. Niños y menores",
          body: (
            <p>
              OrthoPrecision está destinado a profesionales de la salud y no está
              dirigido a niños. La app no debe ser utilizada por menores de edad.
            </p>
          ),
        },
        {
          title: "12. Cambios en esta política",
          body: (
            <p>
              Podemos actualizar esta Política de Privacidad periódicamente. La versión
              más reciente siempre estará disponible en:{" "}
              <a href="https://orthoprecision.app/privacy" className="text-cyan-300 hover:text-cyan-200">
                https://orthoprecision.app/privacy
              </a>
              .
            </p>
          ),
        },
        {
          title: "13. Contacto",
          body: (
            <p>
              Para dudas, solicitudes o cuestiones relacionadas con la privacidad,
              contáctenos en: {emailLink(PRIVACY_EMAIL)}.
            </p>
          ),
        },
      ],
      footerLinks: [
        { label: "Soporte", href: canonicalLinks.support },
        { label: "Términos de Uso", href: canonicalLinks.terms },
        { label: "Política de Privacidad", href: canonicalLinks.privacy },
      ],
    },
  };

  return {
    support: support[language],
    terms: terms[language],
    privacy: privacy[language],
  };
};
